/**
 * Zustand store for license projects.
 *
 * Manages:
 *   - Paginated project list with status filtering
 *   - Project detail (tracks + linked requests)
 *   - Create, update, delete, add/remove tracks, submit
 *   - Realtime subscriptions for live status updates
 *
 * All API calls go through the centralized apiClient (auth headers automatic).
 */
import { create } from "zustand";
import { supabase } from "./supabase";
import { leaveRealtimeChannel, uniqueChannelTopic } from "./realtime";
import { apiClient } from "./api-client";
import type {
  LicenseProject,
  LicenseProjectListItem,
  LicenseProjectDetail,
  LicenseProjectTrack,
  ProjectStatus,
  LicenseProjectListResponse,
  CreateLicenseProjectPayload,
  UpdateLicenseProjectPayload,
  AddProjectTracksPayload,
  AddProjectTracksResponse,
  DuplicateProjectResult,
  ProjectTrackSkipped,
  AddPlaylistToProjectResult,
  CreateProjectFromPlaylistPayload,
  CreateProjectFromPlaylistResult,
  SubmitProjectResponse,
} from "@/types/license-project";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 30;

/**
 * Latest-read token for the open project (A1.12, 2026-10-01). `fetchDetail` had no guard at all:
 * a read still in flight when the buyer moved from project A to B, or signed out, landed after the
 * page had cleared it — A's shortlist under B's address, or one account's project in the next
 * account's view. Every detail read takes the next number and writes only if it is still the
 * newest; `clearDetail` and `reset` move it forward, which retires whatever was in flight.
 */
let detailRead = 0;

/** Statuses of a project the buyer is still working on (everything but completed/archived). */
export const OPEN_PROJECT_STATUSES: ProjectStatus[] = ["draft", "active", "submitted", "negotiating"];

/**
 * One-off list fetch that leaves the store untouched. For surfaces outside /projects (the home
 * panel, the add-to-project picker) that must not inherit whatever status/campaign filter the user
 * last picked there — the store's filters persist across navigation. `total` in the response
 * counts every match, not just the returned page. `q` is the server's search over name and client
 * across every project; `offset` pages on from an earlier read of the same query.
 */
export function fetchProjectsByStatus(
  statuses: ProjectStatus[],
  limit: number,
  { offset = 0, q, sort }: { offset?: number; q?: string; sort?: "recent" } = {}
): Promise<LicenseProjectListResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    status: statuses.join(","),
  });
  const term = q?.trim();
  if (term) params.set("q", term);
  // `recent`: the projects with the most recent activity first (the add-to-project picker);
  // otherwise newest first.
  if (sort) params.set("sort", sort);
  return apiClient.get<LicenseProjectListResponse>(`/api/license-projects?${params}`);
}

// ─────────────────────────────────────────────────────────────────────────────
// Store Interface
// ─────────────────────────────────────────────────────────────────────────────

interface LicenseProjectsState {
  // List view
  projects: LicenseProjectListItem[];
  total: number;
  hasMore: boolean;
  /** Statuses the list is filtered to (one tab can cover several); null = all. */
  statusFilter: ProjectStatus[] | null;
  campaignFilter: string | null; // campaign_id or null for all
  /** Server-side search over name and client; "" = none. */
  searchQuery: string;

  // Detail view
  selectedDetail: LicenseProjectDetail | null;

  // Loading states
  listLoading: boolean;
  loadedOnce: boolean;
  /**
   * A first-page read is out. Unlike listLoading it is set on every read, including a search that
   * keeps the old cards on screen: until it lands, those cards' offset is not the read's, so
   * fetchMore waits.
   */
  firstPageInFlight: boolean;
  /**
   * A search change is waiting for its first page. A search is the only change that keeps the old
   * cards (loadedOnce stays true), so until it lands the list's total is the previous query's and
   * the header and the announced count hold back. A refresh of the same query does not set it.
   */
  searchPending: boolean;
  isFetchingMore: boolean;
  detailLoading: boolean;
  creating: boolean;
  submitting: boolean;

  // Stale result detection
  _generation: number;
  _detailRefreshGen: number;

  // Error states
  listError: string | null;
  /** A failed next page. Kept apart from listError so the cards already loaded stay on screen. */
  loadMoreError: string | null;
  detailError: string | null;

  // ── List actions ──────────────────────────────────────────────────────────
  setStatusFilter: (statuses: ProjectStatus[] | null) => void;
  setSearchQuery: (query: string) => void;
  setCampaignFilter: (campaignId: string | null) => void;
  fetchProjects: () => Promise<void>;
  fetchMore: () => Promise<void>;

  // ── Detail ────────────────────────────────────────────────────────────────
  fetchDetail: (projectId: string) => Promise<void>;
  /**
   * Re-read the open project after an edit so server-derived fields (readiness) catch up.
   * Silent: a failure keeps the locally patched detail rather than showing an error page.
   */
  refreshDetail: (projectId: string) => Promise<void>;
  clearDetail: () => void;

  // ── Mutations ─────────────────────────────────────────────────────────────
  createProject: (payload: CreateLicenseProjectPayload) => Promise<LicenseProjectListItem>;
  updateProject: (projectId: string, payload: UpdateLicenseProjectPayload) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  /** The rows added (a track already in the project is not among them) and the tracks the
   *  backend refused, with the reason (gone, unpublished, no label). */
  addTracks: (
    projectId: string,
    payload: AddProjectTracksPayload
  ) => Promise<{ tracks: LicenseProjectTrack[]; skipped: ProjectTrackSkipped[] }>;
  /** One-time copy of one of the caller's playlists onto the shortlist. */
  addPlaylist: (projectId: string, playlistId: string) => Promise<AddPlaylistToProjectResult>;
  /** A new draft with this project's settings and, by default, its tracks. */
  duplicateProject: (projectId: string, payload?: { name?: string; include_tracks?: boolean }) => Promise<DuplicateProjectResult>;
  /** Create a project and copy a playlist into it, as one server step. */
  createProjectFromPlaylist: (payload: CreateProjectFromPlaylistPayload) => Promise<CreateProjectFromPlaylistResult>;
  /** Archive (decision 13): read-only until unarchived; the default list leaves it out. */
  archiveProject: (projectId: string) => Promise<void>;
  /** Unarchive: back to the stage it had, editable again. */
  unarchiveProject: (projectId: string) => Promise<void>;
  removeTrack: (projectId: string, trackId: string) => Promise<void>;
  updateTrack: (projectId: string, trackRowId: string, updates: { usage_description?: string; music_use_duration?: string; options_requested?: string; notes?: string | null }) => Promise<LicenseProjectTrack>;
  submitProject: (projectId: string) => Promise<SubmitProjectResponse>;

  // ── Realtime ──────────────────────────────────────────────────────────────
  subscribeToChanges: () => () => void;

  // ── Reset ─────────────────────────────────────────────────────────────────
  reset: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Initial State
// ─────────────────────────────────────────────────────────────────────────────

const initialState = {
  projects: [] as LicenseProjectListItem[],
  total: 0,
  hasMore: true,
  statusFilter: null as ProjectStatus[] | null,
  campaignFilter: null as string | null,
  searchQuery: "",
  selectedDetail: null as LicenseProjectDetail | null,
  listLoading: false,
  loadedOnce: false,
  firstPageInFlight: false,
  searchPending: false,
  isFetchingMore: false,
  detailLoading: false,
  creating: false,
  submitting: false,
  _generation: 0,
  _detailRefreshGen: 0,
  listError: null as string | null,
  loadMoreError: null as string | null,
  detailError: null as string | null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useLicenseProjectsStore = create<LicenseProjectsState>(
  (set, get) => ({
    ...initialState,

    // ── List actions ────────────────────────────────────────────────────────

    setStatusFilter: (statuses) => {
      set({ statusFilter: statuses, projects: [], total: 0, hasMore: true, loadedOnce: false });
      get().fetchProjects();
    },

    setSearchQuery: (query) => {
      if (query === get().searchQuery) return;
      // Keep the current cards on screen while the search runs (loadedOnce stays true), so typing
      // doesn't flash the page to a spinner on every debounce.
      set({ searchQuery: query, searchPending: true });
      get().fetchProjects();
    },

    setCampaignFilter: (campaignId) => {
      set({ campaignFilter: campaignId, projects: [], total: 0, hasMore: true, loadedOnce: false });
      get().fetchProjects();
    },

    fetchProjects: async () => {
      const { statusFilter, campaignFilter, searchQuery, loadedOnce } = get();
      const gen = get()._generation + 1;
      set({
        _generation: gen,
        listLoading: !loadedOnce,
        firstPageInFlight: true,
        listError: null,
        loadMoreError: null,
      });

      try {
        const params = new URLSearchParams();
        if (statusFilter?.length) params.set("status", statusFilter.join(","));
        if (searchQuery.trim()) params.set("q", searchQuery.trim());
        if (campaignFilter) params.set("campaign_id", campaignFilter);
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", "0");

        const data = await apiClient.get<LicenseProjectListResponse>(
          `/api/license-projects?${params}`
        );

        // Discard stale results if filters changed while request was in flight
        if (get()._generation !== gen) return;

        set({
          projects: data.items,
          total: data.total,
          hasMore: data.has_more,
          listLoading: false,
          firstPageInFlight: false,
          searchPending: false,
          loadedOnce: true,
        });
      } catch (err: any) {
        if (get()._generation !== gen) return;
        set({
          listError: err.message || "Failed to fetch projects",
          listLoading: false,
          firstPageInFlight: false,
          searchPending: false,
          loadedOnce: true,
        });
      }
    },

    fetchMore: async () => {
      const { projects, hasMore, isFetchingMore, listLoading, statusFilter, campaignFilter, searchQuery, _generation } = get();
      if (!hasMore || isFetchingMore || listLoading || get().firstPageInFlight) return;

      set({ isFetchingMore: true, loadMoreError: null });

      try {
        const params = new URLSearchParams();
        if (statusFilter?.length) params.set("status", statusFilter.join(","));
        if (searchQuery.trim()) params.set("q", searchQuery.trim());
        if (campaignFilter) params.set("campaign_id", campaignFilter);
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(projects.length));

        const data = await apiClient.get<LicenseProjectListResponse>(
          `/api/license-projects?${params}`
        );

        // Discard if filters changed while fetching
        if (get()._generation !== _generation) {
          set({ isFetchingMore: false });
          return;
        }

        set((state) => {
          const existingIds = new Set(state.projects.map((p) => p.id));
          const newItems = data.items.filter((p) => !existingIds.has(p.id));
          return {
            projects: [...state.projects, ...newItems],
            total: data.total,
            hasMore: data.has_more,
            isFetchingMore: false,
          };
        });
      } catch (err: any) {
        // The same stale check as the answer above: an error for a list that has since been
        // re-filtered or wiped (sign-out) is not this list's error.
        if (get()._generation !== _generation) {
          set({ isFetchingMore: false });
          return;
        }
        set({
          loadMoreError: err.message || "Failed to load more projects",
          isFetchingMore: false,
        });
      }
    },

    // ── Detail ──────────────────────────────────────────────────────────────

    fetchDetail: async (projectId) => {
      const read = ++detailRead;
      set({ detailLoading: true, detailError: null });

      try {
        const detail = await apiClient.get<LicenseProjectDetail>(
          `/api/license-projects/${projectId}`
        );
        if (read !== detailRead) return;
        set({ selectedDetail: detail, detailLoading: false });
      } catch (err: any) {
        if (read !== detailRead) return;
        set({
          detailError: err.message || "Failed to fetch project detail",
          detailLoading: false,
        });
      }
    },

    refreshDetail: async (projectId) => {
      const gen = get()._detailRefreshGen + 1;
      set({ _detailRefreshGen: gen });
      try {
        const detail = await apiClient.get<LicenseProjectDetail>(
          `/api/license-projects/${projectId}`
        );
        // A newer refresh started, or the user left this project: drop this response.
        if (get()._detailRefreshGen !== gen || get().selectedDetail?.id !== projectId) return;
        set({ selectedDetail: detail });
      } catch {
        // Keep the locally patched detail; the next edit or visit refreshes it.
      }
    },

    // Closing the open project retires any read of it still in flight, so a late answer cannot
    // reopen it under the next project's address. That read can no longer clear the loading flag,
    // so this does.
    clearDetail: () => {
      detailRead += 1;
      set({ selectedDetail: null, detailError: null, detailLoading: false });
    },

    // ── Mutations ───────────────────────────────────────────────────────────

    createProject: async (payload) => {
      set({ creating: true });

      try {
        const result = await apiClient.post<LicenseProjectListItem>(
          "/api/license-projects",
          payload
        );
        set({ creating: false });

        // Refresh list after creation
        get().fetchProjects();

        return result;
      } catch (err: any) {
        set({ creating: false });
        throw err;
      }
    },

    updateProject: async (projectId, payload) => {
      try {
        // PATCH returns the project row only — no tracks, requests or readiness.
        const updated = await apiClient.patch<LicenseProject>(
          `/api/license-projects/${projectId}`,
          payload
        );

        // Update in list if present
        set((state) => {
          const idx = state.projects.findIndex((p) => p.id === projectId);
          if (idx === -1) return state;
          const next = [...state.projects];
          next[idx] = { ...next[idx], ...updated };
          return { projects: next };
        });

        // Update detail if viewing, then let the server recompute readiness
        const { selectedDetail } = get();
        if (selectedDetail?.id === projectId) {
          set({ selectedDetail: { ...selectedDetail, ...updated } });
          void get().refreshDetail(projectId);
        }
      } catch (err: any) {
        throw err;
      }
    },

    deleteProject: async (projectId) => {
      try {
        await apiClient.delete(`/api/license-projects/${projectId}`);

        // Remove from list
        set((state) => ({
          projects: state.projects.filter((p) => p.id !== projectId),
          total: state.total - 1,
        }));

        // Clear detail if viewing
        const { selectedDetail } = get();
        if (selectedDetail?.id === projectId) {
          set({ selectedDetail: null });
        }
      } catch (err: any) {
        throw err;
      }
    },

    addTracks: async (projectId, payload) => {
      try {
        const result = await apiClient.post<AddProjectTracksResponse>(
          `/api/license-projects/${projectId}/tracks`,
          payload
        );

        // Update detail if viewing this project
        const { selectedDetail } = get();
        if (selectedDetail?.id === projectId) {
          get().fetchDetail(projectId);
        }

        // Update track count in list
        set((state) => {
          const idx = state.projects.findIndex((p) => p.id === projectId);
          if (idx === -1) return state;
          const next = [...state.projects];
          next[idx] = {
            ...next[idx],
            track_count: next[idx].track_count + result.count,
          };
          return { projects: next };
        });

        return { tracks: result.tracks, skipped: result.skipped ?? [] };
      } catch (err: any) {
        throw err;
      }
    },

    addPlaylist: async (projectId, playlistId) => {
      const result = await apiClient.post<AddPlaylistToProjectResult>(
        `/api/license-projects/${projectId}/playlists`,
        { playlist_id: playlistId }
      );
      if (get().selectedDetail?.id === projectId) get().fetchDetail(projectId);
      if (result.added > 0) {
        set((state) => ({
          projects: state.projects.map((p) =>
            p.id === projectId ? { ...p, track_count: p.track_count + result.added } : p
          ),
        }));
      }
      return result;
    },

    duplicateProject: async (projectId, payload = {}) => {
      const result = await apiClient.post<DuplicateProjectResult>(
        `/api/license-projects/${projectId}/duplicate`,
        payload
      );
      get().fetchProjects();
      return result;
    },

    archiveProject: async (projectId) => {
      const row = await apiClient.post<Partial<LicenseProject>>(`/api/license-projects/${projectId}/archive`, {});
      const detail = get().selectedDetail;
      if (detail?.id === projectId) {
        set({ selectedDetail: { ...detail, ...row, status: "archived" } });
        void get().refreshDetail(projectId);
      }
      // The default list leaves archived projects out; the Archived tab lists them.
      void get().fetchProjects();
    },

    unarchiveProject: async (projectId) => {
      const row = await apiClient.post<Partial<LicenseProject>>(`/api/license-projects/${projectId}/unarchive`, {});
      const detail = get().selectedDetail;
      if (detail?.id === projectId) {
        set({ selectedDetail: { ...detail, ...row } });
        void get().refreshDetail(projectId);
      }
      void get().fetchProjects();
    },

    createProjectFromPlaylist: async (payload) => {
      set({ creating: true });
      try {
        const result = await apiClient.post<CreateProjectFromPlaylistResult>(
          "/api/license-projects/from-playlist",
          payload
        );
        get().fetchProjects();
        return result;
      } finally {
        set({ creating: false });
      }
    },

    removeTrack: async (projectId, trackId) => {
      try {
        await apiClient.delete(
          `/api/license-projects/${projectId}/tracks/${trackId}`
        );

        // Update detail if viewing — remove from tracks array
        set((state) => {
          const detail = state.selectedDetail;
          if (!detail || detail.id !== projectId) return state;
          return {
            selectedDetail: {
              ...detail,
              tracks: detail.tracks.filter((t) => t.track_id !== trackId),
            },
          };
        });
        if (get().selectedDetail?.id === projectId) void get().refreshDetail(projectId);

        // Decrement track count in list
        set((state) => {
          const idx = state.projects.findIndex((p) => p.id === projectId);
          if (idx === -1) return state;
          const next = [...state.projects];
          next[idx] = {
            ...next[idx],
            track_count: Math.max(0, next[idx].track_count - 1),
          };
          return { projects: next };
        });
      } catch (err: any) {
        throw err;
      }
    },

    updateTrack: async (projectId, trackRowId, updates) => {
      const result = await apiClient.patch<{ track: LicenseProjectTrack }>(
        `/api/license-projects/${projectId}/tracks/${trackRowId}`,
        updates
      );
      const updatedTrack = result.track;

      // Update in detail if viewing
      set((state) => {
        const detail = state.selectedDetail;
        if (!detail || detail.id !== projectId) return state;
        return {
          selectedDetail: {
            ...detail,
            tracks: detail.tracks.map((t) =>
              t.id === trackRowId ? { ...t, ...updatedTrack } : t
            ),
          },
        };
      });
      if (get().selectedDetail?.id === projectId) void get().refreshDetail(projectId);

      return updatedTrack;
    },

    submitProject: async (projectId) => {
      set({ submitting: true });

      try {
        const result = await apiClient.post<SubmitProjectResponse>(
          `/api/license-projects/${projectId}/submit`
        );
        set({ submitting: false });

        // Refresh the detail only if this project is still the open one — like addTracks. An
        // unconditional read here, landing after the buyer had opened another project, would
        // replace that project with this one.
        if (get().selectedDetail?.id === projectId) {
          get().fetchDetail(projectId);
        }
        get().fetchProjects();

        return result;
      } catch (err: any) {
        set({ submitting: false });
        // 409: another submit of this project (another tab, a teammate) claimed its tracks first.
        // Nothing was created by this one; refetch so the screen shows what the other one sent.
        if (err?.status === 409) {
          if (get().selectedDetail?.id === projectId) get().fetchDetail(projectId);
          get().fetchProjects();
        }
        throw err;
      }
    },

    // ── Realtime ────────────────────────────────────────────────────────────

    subscribeToChanges: () => {
      const channel = supabase
        .channel(uniqueChannelTopic("license-projects"))
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "license_projects",
          },
          (payload) => {
            const { eventType, new: newRow } = payload as any;

            if (eventType === "UPDATE" && newRow) {
              set((state) => {
                const idx = state.projects.findIndex((p) => p.id === newRow.id);
                if (idx === -1) return state;
                const next = [...state.projects];
                next[idx] = { ...next[idx], ...newRow };
                return { projects: next };
              });

              const { selectedDetail } = get();
              if (selectedDetail?.id === newRow.id) {
                set({ selectedDetail: { ...selectedDetail, ...newRow } });
              }
            }

            if (eventType === "INSERT") {
              get().fetchProjects();
            }

            if (eventType === "DELETE") {
              const oldRow = (payload as any).old;
              if (oldRow?.id) {
                set((state) => ({
                  projects: state.projects.filter((p) => p.id !== oldRow.id),
                }));
              }
            }
          }
        )
        .subscribe();

      // Only this channel: removeChannel() would close every channel in the app (lib/realtime.ts).
      return () => leaveRealtimeChannel(channel);
    },

    // ── Reset ───────────────────────────────────────────────────────────────

    // `_generation` moves FORWARD, never back to 0. A list fetch still in flight for the account
    // being signed out holds the generation it started with; zeroing it let the next account's
    // first fetch reuse that same number, so the old answer passed the stale check and wrote the
    // previous account's projects over the new account's list. The open project's read is
    // retired the same way (`detailRead`).
    reset: () => {
      detailRead += 1;
      set((state) => ({ ...initialState, _generation: state._generation + 1 }));
    },
  })
);

/**
 * The newest projects in every status but archived (the server's default list, decision 13), and
 * how many there are in all (`total` counts every such project, not the page). Home's projects
 * panel reads this, never the store: the store's list carries the search, status and campaign
 * filter last used on /projects, so a search there showed up on Home with its match count passed
 * off as the organization's total (list-1, 2026-10-19). A request of its own with no filter at all,
 * so nothing picked on /projects can reach it.
 */
export function fetchRecentProjects(limit: number): Promise<LicenseProjectListResponse> {
  const params = new URLSearchParams({ limit: String(limit), offset: "0" });
  return apiClient.get<LicenseProjectListResponse>(`/api/license-projects?${params}`);
}

/**
 * How many archived projects there are (the Archived tab's own list, one row read for its
 * `total`). Home asks only when the default list is empty, so it never says "No projects yet" to
 * a team whose projects are all archived (review Q8-14).
 */
export async function fetchArchivedProjectCount(): Promise<number> {
  const params = new URLSearchParams({ status: "archived", limit: "1", offset: "0" });
  const page = await apiClient.get<LicenseProjectListResponse>(`/api/license-projects?${params}`);
  return page.total;
}
